<script setup lang="ts">
import axios from 'axios'



const TB_BASE_URL = import.meta.env.VITE_TB_URL

interface TestResult {
  label: string
  endpoint: string
  status: 'idle' | 'loading' | 'success' | 'error'
  data: unknown
  error: string
  requestInfo: {
    url: string
    headers: Record<string, string>
    responseStatus: number | null
  } | null
}

const isLoading = ref(false)
const tbToken = ref<string | null>(null)
const loginResponse = ref<unknown>(null)
const loginError = ref('')

const { token: keycloakToken } = useKeycloak()
const keycloakTokenCopied = ref(false)
async function copyKeycloakToken() {
  if (!keycloakToken.value) return
  await navigator.clipboard.writeText(keycloakToken.value)
  keycloakTokenCopied.value = true
  setTimeout(() => { keycloakTokenCopied.value = false }, 2000)
}

const tests = ref<TestResult[]>([
  { label: 'Utilisateur courant',      endpoint: '/api/auth/user',                           status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Heure serveur',            endpoint: '/api/dashboard/serverTime',                 status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Info usage tenant',        endpoint: '/api/usage',                                status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Appareils (5 premiers)',   endpoint: '/api/tenant/devices?pageSize=5&page=0',     status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Clients (5 premiers)',     endpoint: '/api/customers?pageSize=5&page=0',           status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Assets (5 premiers)',      endpoint: '/api/tenant/assets?pageSize=5&page=0',      status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Types d\'appareils',       endpoint: '/api/device/types',                         status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Types d\'assets',          endpoint: '/api/asset/types',                          status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Dashboards (5 premiers)',  endpoint: '/api/tenant/dashboards?pageSize=5&page=0',  status: 'idle', data: null, error: '', requestInfo: null },
  { label: 'Profils d\'appareils',     endpoint: '/api/deviceProfiles?pageSize=5&page=0',     status: 'idle', data: null, error: '', requestInfo: null },
])

async function runTest(test: TestResult) {
  if (!tbToken.value) return
  test.status = 'loading'
  test.data = null
  test.error = ''
  test.requestInfo = null

  const url = `${TB_BASE_URL}${test.endpoint}`
  const headers: Record<string, string> = {
    'X-Authorization': `Bearer ${tbToken.value}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }

  try {
    const response = await axios.get(url, { headers })
    test.requestInfo = { url, headers, responseStatus: response.status }
    test.data = response.data
    test.status = 'success'
  } catch (e: unknown) {
    const err = e as { response?: { status: number; data: unknown }; message?: string }
    test.requestInfo = { url, headers, responseStatus: err.response?.status ?? null }
    test.error = err.response
      ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
      : (err.message ?? String(e))
    test.status = 'error'
  }
}

async function runAllTests() {
  for (const test of tests.value) {
    await runTest(test)
  }
}

const theme = useTheme()

onMounted(async () => {
  isLoading.value = true
  loginError.value = ''
  try {
    const response = await apiOmni.post('api/tb/login')
    tbToken.value = response.data.token
    loginResponse.value = response.data
    await runAllTests()
  } catch (e: unknown) {
    const err = e as { response?: { status: number; data: unknown }; message?: string }
    loginError.value = err.response
      ? `HTTP ${err.response.status}: ${JSON.stringify(err.response.data)}`
      : (err.message ?? String(e))
    Notify.error(loginError.value)
  } finally {
    isLoading.value = false
  }
})

const statusColor = (status: TestResult['status']) => {
  switch (status) {
    case 'success': return 'success'
    case 'error':   return 'error'
    case 'loading': return 'warning'
    default:        return 'grey'
  }
}
const statusIcon = (status: TestResult['status']) => {
  switch (status) {
    case 'success': return 'M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z'
    case 'error':   return 'M13,13H11V7H13M13,17H11V15H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z'
    case 'loading': return 'M12,4V2A10,10 0 0,0 2,12H4A8,8 0 0,1 12,4Z'
    default:        return 'M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z'
  }
}
</script>

<template>
  <v-card class="mx-4 my-4 page-card" rounded="xl" :loading="isLoading">

    <div class="page-header px-5 pt-5 pb-3">
      <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 12px;">
        <div class="d-flex align-center" style="gap: 12px;">
          <v-avatar color="primary" variant="tonal" size="44" rounded="lg">
            <v-icon icon="M7 7H5A2 2 0 0 0 3 9V17H5V13H7V17H9V9A2 2 0 0 0 7 7M7 11H5V9H7M14 7H10V17H12V13H14A2 2 0 0 0 16 11V9A2 2 0 0 0 14 7M14 11H12V9H14M20 9V15H21V17H17V15H18V9H17V7H21V9Z" size="26"/>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-bold" style="line-height: 1.2;">ThingsBoard</div>
            <div class="text-caption text-medium-emphasis">Test de connexion — {{ TB_BASE_URL }}</div>
          </div>
        </div>
        <div class="d-flex align-center" style="gap: 8px;">
          <v-btn
            color="primary"
            variant="tonal"
            rounded="pill"
            prepend-icon="M17.65,6.35C16.2,4.9 14.21,4 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20C15.73,20 18.84,17.45 19.73,14H17.65C16.83,16.33 14.61,18 12,18A6,6 0 0,1 6,12A6,6 0 0,1 12,6C13.66,6 15.14,6.69 16.22,7.78L13,11H20V4L17.65,6.35Z"
            :loading="isLoading"
            @click="runAllTests"
          >
            Relancer les tests
          </v-btn>
        </div>
      </div>
    </div>

    <v-divider/>

    <div style="flex: 1 1 auto; overflow-y: auto;" class="pa-4">

      <!-- Login status -->
      <v-alert v-if="loginError" type="error" variant="tonal" density="compact" class="mb-4">
        Erreur de connexion proxy : {{ loginError }}
      </v-alert>

      <v-row v-if="tbToken" class="mb-4">
        <v-col cols="12" md="6">
          <v-alert type="success" variant="tonal" density="compact" icon="M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z" class="mb-2">
            Token TB obtenu via <code>api/tb/login</code>
          </v-alert>
          <v-card variant="outlined" density="compact">
            <v-card-title class="text-body-2">
              <v-icon size="16" class="mr-1">M7 7H5A2 2 0 0 0 3 9V17H5V13H7V17H9V9A2 2 0 0 0 7 7M7 11H5V9H7M14 7H10V17H12V13H14A2 2 0 0 0 16 11V9A2 2 0 0 0 14 7M14 11H12V9H14M20 9V15H21V17H17V15H18V9H17V7H21V9Z</v-icon>
              Réponse complète api/tb/login
            </v-card-title>
            <v-card-text>
              <pre
                class="text-caption overflow-auto pa-2 rounded"
                style="white-space: pre-wrap; word-break: break-all; background: rgba(128,128,128,0.1); max-height: 250px;"
              >{{ JSON.stringify(loginResponse, null, 2) }}</pre>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Keycloak token for Swagger -->
      <v-row class="mb-4">
        <v-col cols="12" md="6">
          <v-card variant="outlined" density="compact">
            <v-card-title class="text-body-2">
              <v-icon size="16" class="mr-1">M12,8A1,1 0 0,1 13,9A1,1 0 0,1 12,10A1,1 0 0,1 11,9A1,1 0 0,1 12,8M21,11C21,16.55 17.16,21.74 12,23C6.84,21.74 3,16.55 3,11V5L12,1L21,5V11M12,6A3,3 0 0,0 9,9C9,10.31 9.83,11.42 11,11.83V18H13V16H15V14H13V11.83C14.17,11.42 15,10.31 15,9A3,3 0 0,0 12,6Z</v-icon>
              JWT Keycloak (pour Swagger)
            </v-card-title>
            <v-card-text>
              <div
                class="pa-2 rounded text-caption"
                style="font-family: monospace; word-break: break-all; white-space: pre-wrap; background: rgba(128,128,128,0.1); max-height: 60px; overflow: auto;"
              >{{ keycloakToken || 'Chargement…' }}</div>
            </v-card-text>
            <v-card-actions>
              <v-btn
                size="small"
                :color="keycloakTokenCopied ? 'success' : 'default'"
                :prepend-icon="keycloakTokenCopied ? 'M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z' : 'M19,21H8V7H19M19,5H8A2,2 0 0,0 6,7V21A2,2 0 0,0 8,23H19A2,2 0 0,0 21,21V7A2,2 0 0,0 19,5M16,1H4A2,2 0 0,0 2,3V17H4V3H16V1Z'"
                variant="text"
                @click="copyKeycloakToken"
              >
                {{ keycloakTokenCopied ? 'Copié !' : 'Copier' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Test results -->
      <v-row>
        <v-col
          v-for="test in tests"
          :key="test.endpoint"
          cols="12"
          md="6"
        >
          <v-card variant="outlined">
            <v-card-title class="d-flex align-center text-body-1">
              <v-icon
                :color="statusColor(test.status)"
                :icon="statusIcon(test.status)"
                class="mr-2"
                size="20"
              />
              {{ test.label }}
            </v-card-title>
            <v-card-subtitle class="text-caption" style="font-family: monospace;">
              GET {{ test.endpoint }}
            </v-card-subtitle>
            <v-card-text>
              <template v-if="test.requestInfo">
                <div class="text-caption text-medium-emphasis mb-1 font-weight-bold">Requête</div>
                <pre
                  class="text-caption pa-2 rounded mb-3"
                  style="white-space: pre-wrap; word-break: break-all; background: rgba(128,128,128,0.1);"
                >GET {{ test.requestInfo.url }}

Headers:
{{ JSON.stringify(test.requestInfo.headers, null, 2) }}

Status: {{ test.requestInfo.responseStatus }}</pre>
                <v-divider class="mb-2"/>
              </template>
              <div class="text-caption text-medium-emphasis mb-1 font-weight-bold">Réponse</div>
              <v-alert
                v-if="test.status === 'error'"
                type="error"
                variant="tonal"
                density="compact"
              >
                {{ test.error }}
              </v-alert>
              <pre
                v-else-if="test.data !== null"
                class="text-caption overflow-auto pa-2 rounded"
                style="max-height: 200px; white-space: pre-wrap; word-break: break-all; background: rgba(128,128,128,0.1);"
              >{{ JSON.stringify(test.data, null, 2) }}</pre>
              <span v-else class="text-medium-emphasis text-caption">En attente…</span>
            </v-card-text>
            <v-card-actions>
              <v-btn
                size="small"
                variant="text"
                :disabled="!tbToken || test.status === 'loading'"
                :loading="test.status === 'loading'"
                prepend-icon="M8,5.14V19.14L19,12.14L8,5.14Z"
                @click="runTest(test)"
              >
                Retester
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

    </div>
  </v-card>
</template>

<style scoped>
.page-header {
  background: v-bind('theme.global.name.value === "dark" ? "rgba(var(--v-theme-surface-variant), 0.25)" : "rgba(var(--v-theme-primary), 0.04)"');
}
</style>
